import { supabase } from "@/integrations/supabase/client";

/** One-row-per-currency rollup of an affiliate's commissions. */
export type AffiliateDashboardStatRow = {
  currency: string;
  pending_cents: number;
  approved_cents: number;
  paid_cents: number;
  forfeited_cents: number;
  reversed_cents: number;
};

/** Single row from `affiliate_commissions` as the dashboard renders it. */
export type AffiliateCommissionRow = {
  id: string;
  affiliate_user_id: string;
  affiliate_profile_id: string | null;
  is_super_affiliate: boolean;
  referred_user_id: string;
  stripe_event_id: string | null;
  stripe_invoice_id: string | null;
  stripe_subscription_id: string | null;
  gross_amount_cents: number;
  commission_bps: number;
  commission_amount_cents: number;
  currency: string;
  status: "pending" | "approved" | "reversed" | "paid" | "forfeited";
  accrued_at: string;
  approved_at: string | null;
  reversed_at: string | null;
  forfeited_at: string | null;
  paid_at: string | null;
  reverses_commission_id: string | null;
  notes: string | null;
};

export const COMMISSION_STATUSES: AffiliateCommissionRow["status"][] = [
  "pending",
  "approved",
  "reversed",
  "paid",
  "forfeited",
];

/** Format cents as a localized currency string. */
export function formatCommissionAmount(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** Mask a uuid into a short, non-PII label like `user_8f2a1b3c`. */
export function maskReferredUserId(id: string): string {
  return `user_${id.replace(/-/g, "").slice(0, 8)}`;
}

/** Expected approval = accrued_at + holdback_days. */
export function expectedApprovalDate(accruedAtIso: string, holdbackDays: number): Date {
  const d = new Date(accruedAtIso);
  d.setUTCDate(d.getUTCDate() + holdbackDays);
  return d;
}

/** Fetch per-currency stats for a Persona profile. */
export async function fetchDashboardStats(profileId: string): Promise<AffiliateDashboardStatRow[]> {
  const { data, error } = await supabase.rpc("get_affiliate_dashboard_stats", {
    _profile_id: profileId,
  });
  if (error) throw error;
  return (data ?? []) as AffiliateDashboardStatRow[];
}

/** Fetch the Persona's own commission rows, newest first. */
export async function fetchMyCommissions(
  profileId: string,
  opts: { status?: AffiliateCommissionRow["status"] | "all"; limit?: number } = {},
): Promise<AffiliateCommissionRow[]> {
  let q = supabase
    .from("affiliate_commissions")
    .select(
      "id, affiliate_user_id, affiliate_profile_id, is_super_affiliate, referred_user_id, stripe_event_id, stripe_invoice_id, stripe_subscription_id, gross_amount_cents, commission_bps, commission_amount_cents, currency, status, accrued_at, approved_at, reversed_at, forfeited_at, paid_at, reverses_commission_id, notes",
    )
    .eq("affiliate_profile_id", profileId)
    .order("accrued_at", { ascending: false })
    .limit(opts.limit ?? 100);

  if (opts.status && opts.status !== "all") {
    q = q.eq("status", opts.status);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as AffiliateCommissionRow[];
}

/** Days between two dates, floored. Returns 0 if `to` precedes `from`. */
export function daysBetween(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}
